/**
 * Строки вкладки «Характеристики»: витрина + значение ПИМ + откуда значение.
 *
 * Список берётся из справочника площадки (деталь окна: обязательность и имена),
 * значения ПИМ и признаки `locked`/`overridden` — из состояния окна по
 * `target_id`. Характеристика, которой нет в состоянии окна (нет справочника
 * или нет в зеркале), остаётся только для чтения: добавить её из окна нельзя.
 */

import { characteristicText, type McardCharacteristic } from './model'
import type { CardWindowState } from './windowEditApi'

export type CharacteristicOrigin = 'platform' | 'pim_override' | 'unknown' | 'ozon_category_type'

export interface CharacteristicRow {
  key: string
  targetId: number | null
  name: string
  required: boolean
  /** Что на витрине. */
  platformValue: string
  /** Значение ПИМ (с правками); null — состояния окна нет. */
  pimValue: string | null
  overridden: boolean
  /** Откуда значение, которое видит менеджер. */
  origin: CharacteristicOrigin
  /** Можно править из окна. */
  editable: boolean
  /** Почему править нельзя (для всплывающей подсказки). */
  readOnlyReason: string | null
  hint?: string
}

export const ORIGIN_LABELS: Record<CharacteristicOrigin, string> = {
  platform: 'Витрина',
  pim_override: 'Правка в ПИМ',
  unknown: 'нет данных',
  ozon_category_type: 'Тип категории Ozon',
}

export function buildCharacteristicRows(
  characteristics: McardCharacteristic[],
  state: CardWindowState | null,
): CharacteristicRow[] {
  const fromState = new Map<number, CardWindowState['fields']['characteristics'][number]>()
  for (const item of state?.fields.characteristics ?? []) {
    if (item.target_id !== null && item.target_id !== undefined) fromState.set(Number(item.target_id), item)
  }
  const used = new Set<number>()

  const rows: CharacteristicRow[] = characteristics.map((item) => {
    const stateItem = item.targetId !== null ? fromState.get(item.targetId) : undefined
    if (item.targetId !== null && stateItem) used.add(item.targetId)
    let editable = false
    let reason: string | null = null
    if (item.system) reason = item.system.lockedReason
    else if (!state) reason = 'правка недоступна: нет связи с карточкой ПИМ'
    else if (!stateItem) reason = 'добавить характеристику из окна нельзя: её нет в карточке на площадке'
    else if (stateItem.locked) reason = stateItem.locked_reason || 'характеристика заблокирована для правки из окна'
    else editable = true
    return {
      key: item.key,
      targetId: item.targetId,
      name: item.name,
      required: item.required,
      platformValue: item.value,
      pimValue: !item.system && stateItem ? characteristicText(stateItem.value) : null,
      overridden: !item.system && Boolean(stateItem?.overridden),
      origin: item.system?.source ?? (stateItem ? (stateItem.overridden ? 'pim_override' : 'platform') : state ? 'platform' : 'unknown'),
      editable,
      readOnlyReason: reason,
      hint: item.hint,
    }
  })

  // Характеристики, которые знает состояние, но нет в справочнике окна.
  for (const [targetId, stateItem] of fromState) {
    if (used.has(targetId)) continue
    rows.push({
      key: `state-${targetId}`,
      targetId,
      name: stateItem.name,
      required: false,
      platformValue: characteristicText(stateItem.platform_value),
      pimValue: characteristicText(stateItem.value),
      overridden: stateItem.overridden,
      origin: stateItem.overridden ? 'pim_override' : 'platform',
      editable: !stateItem.locked,
      readOnlyReason: stateItem.locked ? stateItem.locked_reason || 'характеристика заблокирована для правки из окна' : null,
    })
  }
  return rows
}

/** Значение, которое сейчас видит менеджер: ПИМ с правками, иначе витрина. */
export const effectiveValue = (row: CharacteristicRow): string => (row.pimValue !== null ? row.pimValue : row.platformValue)

/** Обязательных характеристик без значения на витрине. */
export const countMissingRequired = (characteristics: McardCharacteristic[]): number =>
  characteristics.filter((item) => item.required && !item.value.trim()).length
